import type { FileObject } from '~~/shared-components/types/Objects'

type ApiClient = ReturnType<typeof useApi>

/**
 * Rozpozná pending media hodnotu (url nebo base64 data URL) uloženou v bloku
 * (data.pendingImage / data.pendingDocument), kterou je potřeba při save nahrát a nahradit za id.
 */
export const isPendingMedia = (value: unknown): value is string =>
  typeof value === 'string' && (value.startsWith('data:') || /^(https?:)?\/\//.test(value))

/**
 * AI dialekt odkazu na existující dokument galerie („document:123") v pending vstupech —
 * na rozdíl od URL se nic nenahrává, FE si stáhne FileObject a použije ho přímo.
 */
export const parseDocumentRef = (value: unknown): number | null => {
  const match = typeof value === 'string' ? value.trim().match(/^document:(\d+)$/) : null
  return match ? Number(match[1]) : null
}

/**
 * Syntetický FileObject pro odložený upload mimo content editor (AI-nastavený hlavní obrázek
 * entity, obrázky galerie). Náhled vykreslí existující komponenty z `paths`/`objectURL`
 * (vzdálená URL — thumby na BE neexistují, proto všechny velikosti míří na originál);
 * při save ho resolve hook nahraje a nahradí reálným FileObjectem podle markeru `pendingMedia`.
 */
export type PendingFileObject = FileObject & { pendingMedia: string; objectURL: string }

export const isPendingFileObject = (value: unknown): value is PendingFileObject =>
  !!value && typeof value === 'object' && isPendingMedia((value as PendingFileObject).pendingMedia)

export const createPendingFileObject = (url: string): PendingFileObject => {
  const uuid = crypto.randomUUID()
  const filename = url.startsWith('data:')
    ? 'nahled'
    : url.split(/[?#]/)[0]?.split('/').at(-1) || 'nahled'
  return {
    pendingMedia: url,
    objectURL: url,
    uuid,
    id: 0,
    key: null,
    guid: null,
    uploadCode: uuid,
    title: filename.split('.').slice(0, -1).join('.') || filename,
    slug: null,
    filename,
    uploaded: true,
    proccessing: false,
    isAudio: false,
    orientation: null,
    mimetypeOrigin: null,
    typeGroup: 'image',
    params: { px: 0.5, py: 0.5 },
    isVideo: false,
    isImage: true,
    mimetypeOriginal: '',
    size: { human: null, raw: 0, unit: 'B' },
    resolution: { unit: 'px', height: null, width: null },
    paths: {
      original: url,
      download: url,
      thumb: url,
      thumbs: { xs: url, s: url, m: url, l: url, xl: url, '2xl': url },
    },
  }
}

const mimeToExtension = (mime: string): string => mime.split('/')[1]?.split('+')[0] || 'bin'

/** Ověří, že mimetype odpovídá `accept` (např. "image/*", "video/*,image/png"). */
export const acceptMatches = (mime: string, accept?: string): boolean => {
  if (!accept) return true
  if (!mime) return false
  return accept.split(',').some((raw) => {
    const a = raw.trim()
    if (!a) return false
    if (a === '*' || a === '*/*') return true
    if (a.endsWith('/*')) return mime.startsWith(a.slice(0, -1))
    return mime === a
  })
}

/**
 * Odhadne `type` pro video.js `sources` z URL streamu/souboru
 * (HLS .m3u8, DASH .mpd, mp4/webm…). Neznámou příponu bere jako HLS (nejčastější stream).
 */
export const streamMimeFromUrl = (url: string): string => {
  if (url.startsWith('data:')) return url.slice(5).split(/[;,]/)[0] || 'video/mp4'
  const path = url.split(/[?#]/)[0]?.toLowerCase() ?? ''
  if (path.endsWith('.m3u8')) return 'application/x-mpegURL'
  if (path.endsWith('.mpd')) return 'application/dash+xml'
  if (path.endsWith('.webm')) return 'video/webm'
  if (path.endsWith('.ogv')) return 'video/ogg'
  if (path.endsWith('.mov')) return 'video/quicktime'
  if (path.endsWith('.mp4') || path.endsWith('.m4v')) return 'video/mp4'
  return 'application/x-mpegURL'
}

/**
 * Převede pending hodnotu na File připravený k uploadu.
 * base64 data URL → Blob přes fetch; URL → Blob přes BE proxy (obchází CORS).
 */
export const pendingMediaToFile = async (
  value: string,
  { get }: Pick<ApiClient, 'get'>
): Promise<File> => {
  if (value.startsWith('data:')) {
    const blob = await (await fetch(value)).blob()
    return new File([blob], `upload-${crypto.randomUUID()}.${mimeToExtension(blob.type)}`, {
      type: blob.type,
    })
  }
  const blob = await get<undefined, undefined, Blob>(
    `/public/documents/proxy?url=${encodeURIComponent(value)}`
  )
  const filename = value.split('?')[0]?.split('/').at(-1) || `upload-${crypto.randomUUID()}`
  return new File([blob], filename, { type: blob.type })
}
